import { getDefaultBufferedPageContext } from '@segment/analytics-page-tools';

/*
 * Segment client with MTU (Monthly Tracked User) protection.
 *
 * Segment bills every new anonymousId that sends even one event. Bots and
 * headless renderers never keep cookies, so each crawl of each URL used to
 * mint a fresh MTU. In Sep 2026 anonymous visitors were 43k of 54k MTUs.
 *
 * 1. Bot gate: known crawlers / automation get a no-op client and never
 *    download the SDK.
 * 2. Engagement gate: everyone else gets a queue. Nothing is sent until the
 *    visitor shows they are a person (pointer, key, wheel or touch input, or
 *    DWELL_MS of the tab being visible). Returning visitors who already carry
 *    a Segment anonymousId skip the gate, since they cost no new identity.
 * 3. Lazy SDK: @segment/analytics-next is only downloaded once the gate
 *    opens, which also keeps it out of the main bundle.
 *
 * Queued calls replay with the page context captured when they were made, so
 * the landing URL, UTM params (context.campaign) and referrer are preserved
 * even if the visitor has navigated by the time the SDK loads.
 *
 * Consequence: visitors who bounce within DWELL_MS without any input are not
 * tracked. Segment/Mixpanel bounce rates and raw pageview counts will drop.
 */

const DWELL_MS = 5000;
const INPUT_EVENTS = ['pointerdown', 'mousemove', 'keydown', 'wheel', 'touchstart'];
const SEGMENT_ANON_ID_KEY = 'ajs_anonymous_id';

// "bot" covers Googlebot, bingbot, GPTBot, AhrefsBot, etc. CUBOT (an Android
// phone brand) is stripped first so its owners are not treated as bots.
const BOT_UA =
  /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|gtmetrix|ptst|pingdom|uptime|statuscake|site24x7|inspectiontool|googleother|mediapartners|facebookexternalhit|bytespider|ahrefs|semrush|yandex|baidu|petalbot|chatgpt|phantomjs|selenium|puppeteer|playwright/i;

const isBrowser = typeof window !== 'undefined';
const writeKey = process.env.GATSBY_SEGMENT_ID || process.env.SEGMENT_ID;

const noop = () => {};
const noopAnalytics = {
  track: noop,
  page: noop,
  identify: noop,
  user: () => ({ anonymousId: () => null }),
};

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : undefined;
}

const fbCookiePlugin = {
  name: 'Facebook Cookie Enrichment',
  type: 'enrichment',
  version: '1.0.0',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    if (fbp) ctx.updateEvent('properties.fbp', fbp);
    if (fbc) ctx.updateEvent('properties.fbc', fbc);
    return ctx;
  },
  page: (ctx) => {
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    if (fbp) ctx.updateEvent('properties.fbp', fbp);
    if (fbc) ctx.updateEvent('properties.fbc', fbc);
    return ctx;
  },
  identify: (ctx) => ctx,
};

export function isLikelyBot() {
  const nav = window.navigator || {};
  if (nav.webdriver) return true;
  const ua = String(nav.userAgent || '').replace(/cubot/gi, '');
  if (BOT_UA.test(ua)) return true;
  // Old headless Chrome and many scrapers report no languages.
  if (nav.languages && nav.languages.length === 0) return true;
  if (window.callPhantom || window._phantom || window.__nightmare) return true;
  return false;
}

function isReturningVisitor() {
  try {
    return Boolean(
      getCookie(SEGMENT_ANON_ID_KEY) ||
        window.localStorage.getItem(SEGMENT_ANON_ID_KEY),
    );
  } catch (e) {
    return false;
  }
}

function createGatedAnalytics() {
  const queue = [];
  let instance = null;
  let started = false;
  let resolveReady;
  const ready = new Promise((resolve) => {
    resolveReady = resolve;
  });

  let dwellTimer = null;
  let visibleSince = null;
  let visibleMs = 0;

  function call(method, args) {
    if (instance) {
      instance[method](...args);
      return;
    }
    queue.push({ method, args: [...args, getDefaultBufferedPageContext()] });
  }

  function disarm() {
    INPUT_EVENTS.forEach((type) =>
      window.removeEventListener(type, start, { capture: true }),
    );
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('prerenderingchange', onVisibilityChange);
    clearTimeout(dwellTimer);
  }

  function start() {
    if (started) return;
    started = true;
    disarm();

    import(/* webpackChunkName: "segment" */ '@segment/analytics-next')
      .then(({ AnalyticsBrowser }) =>
        AnalyticsBrowser.load(
          { writeKey, cdnURL: 'https://sapi.quashed.co.nz/cdn' },
          {
            integrations: {
              'Segment.io': {
                apiHost: 'sapi.quashed.co.nz/v1',
                protocol: 'https',
              },
            },
          },
        ),
      )
      .then(async ([a]) => {
        await a.register(fbCookiePlugin);
        window.analytics = a;
        instance = a;
        queue.splice(0).forEach(({ method, args }) => a[method](...args));
        resolveReady([a]);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Segment failed to load', err);
      });
  }

  // Counts only time the tab is actually visible (not prerendered or in a
  // background tab), and pauses while hidden.
  function onVisibilityChange() {
    const visible =
      document.visibilityState === 'visible' && !document.prerendering;
    if (visible && visibleSince === null) {
      visibleSince = Date.now();
      dwellTimer = setTimeout(start, Math.max(0, DWELL_MS - visibleMs));
    } else if (!visible && visibleSince !== null) {
      visibleMs += Date.now() - visibleSince;
      visibleSince = null;
      clearTimeout(dwellTimer);
    }
  }

  if (isReturningVisitor()) {
    start();
  } else {
    INPUT_EVENTS.forEach((type) =>
      window.addEventListener(type, start, { capture: true, passive: true }),
    );
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('prerenderingchange', onVisibilityChange);
    onVisibilityChange();
  }

  return {
    track: (...args) => call('track', args),
    page: (...args) => call('page', args),
    identify: (...args) => call('identify', args),
    // Thenable so `await analytics` / `analytics.then(([a]) => ...)` keep
    // working. Resolves only once the SDK has loaded (i.e. after engagement).
    then: (onFulfilled, onRejected) => ready.then(onFulfilled, onRejected),
  };
}

function createNoopAnalytics() {
  return {
    ...noopAnalytics,
    then: (onFulfilled, onRejected) =>
      Promise.resolve([noopAnalytics]).then(onFulfilled, onRejected),
  };
}

export const analytics =
  isBrowser && writeKey && !isLikelyBot()
    ? createGatedAnalytics()
    : createNoopAnalytics();
